import React, { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ContactSection = () => {
  const { theme } = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    concern: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    privacy: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const concernOptions = [
    { value: 'appointment', label: 'Termin anfragen' },
    { value: 'question', label: 'Allgemeine Frage' },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.concern) newErrors.concern = 'Bitte wählen Sie ein Anliegen aus';
    if (!formData.name.trim()) newErrors.name = 'Bitte geben Sie Ihren Namen ein';

    if (!formData.email.trim()) {
      newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (!formData.message.trim()) newErrors.message = 'Bitte geben Sie eine Nachricht ein';
    if (!formData.privacy) newErrors.privacy = 'Bitte stimmen Sie der Datenschutzerklärung zu';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // ── Geändert: axios → fetch, /api/contact → /contact.php ──
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concern: formData.concern,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          privacy: formData.privacy,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          concern: '',
          name: '',
          email: '',
          phone: '',
          message: '',
          privacy: false,
        });
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <h2
              className="font-serif text-3xl md:text-4xl mb-4"
              style={{ color: theme?.colors?.text?.primary || 'var(--color-text-primary)' }}
            >
              Kontakt aufnehmen
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Der erste Schritt ist oft der wichtigste. Ich freue mich darauf, Sie kennenzulernen und gemeinsam
              herauszufinden, wie ich Sie unterstützen kann.
            </p>
            <div
              className="w-24 h-1 mx-auto rounded-full mt-4"
              style={{ backgroundColor: 'var(--color-accent-500)' }}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Kontaktformular */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Anliegen */}
                <div>
                  <label className="form-label">
                    Ihr Anliegen <span className="text-red-500">*</span>
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {concernOptions.map((option) => {
                      const selected = formData.concern === option.value;

                      return (
                        <label
                          key={option.value}
                          className="flex items-center p-3 rounded-lg cursor-pointer transition-all"
                          style={{
                            border: `1px solid ${
                              selected ? 'var(--color-primary-500)' : 'var(--color-border-subtle)'
                            }`,
                            backgroundColor: selected ? 'var(--color-bg-tertiary)' : 'transparent',
                          }}
                        >
                          <input
                            type="radio"
                            name="concern"
                            value={option.value}
                            checked={selected}
                            onChange={handleInputChange}
                            className="sr-only"
                          />

                          <span
                            className="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
                            style={{
                              borderColor: selected ? 'var(--color-primary-500)' : 'rgba(0,0,0,0.35)',
                            }}
                          >
                            {selected && (
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: 'var(--color-primary-500)' }}
                              />
                            )}
                          </span>

                          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                            {option.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {errors.concern && <p className="form-error">{errors.concern}</p>}
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`form-input ${errors.name ? 'border-red-300' : ''}`}
                    placeholder="Kim Beispiel"
                  />
                  {errors.name && <p className="form-error">{errors.name}</p>}
                </div>

                {/* E-Mail */}
                <div>
                  <label htmlFor="email" className="form-label">
                    E-Mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'border-red-300' : ''}`}
                    placeholder="kim.beispiel@email.de"
                  />
                  {errors.email && <p className="form-error">{errors.email}</p>}
                </div>

                {/* Telefon */}
                <div>
                  <label htmlFor="phone" className="form-label">
                    Telefonnummer (optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="0351 / 123 456 78"
                  />
                </div>

                {/* Nachricht */}
                <div>
                  <label htmlFor="message" className="form-label">
                    Nachricht <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className={`form-input resize-none ${errors.message ? 'border-red-300' : ''}`}
                    placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                  />
                  {errors.message && <p className="form-error">{errors.message}</p>}
                </div>

                {/* Datenschutz */}
                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleInputChange}
                      className="mt-1 h-4 w-4 rounded"
                      style={{ accentColor: 'var(--color-primary-500)' }}
                    />
                    <span className="ml-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Ich habe die{' '}
                      <a href="/datenschutz" target="_blank" rel="noreferrer">
                        Datenschutzerklärung
                      </a>{' '}
                      gelesen und bin mit der Verarbeitung meiner Daten einverstanden.{' '}
                      <span className="text-red-500">*</span>
                    </span>
                  </label>
                  {errors.privacy && <p className="form-error">{errors.privacy}</p>}
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn btn-primary w-full ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <span className="spinner mr-2"></span>
                        Wird gesendet...
                      </span>
                    ) : (
                      'Nachricht senden'
                    )}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(34,197,94,0.10)',
                      border: '1px solid rgba(34,197,94,0.25)',
                      color: 'rgba(22,101,52,1)',
                    }}
                  >
                    ✓ Vielen Dank für Ihre Nachricht! Ich werde mich schnellstmöglich bei Ihnen melden.
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(239,68,68,0.10)',
                      border: '1px solid rgba(239,68,68,0.25)',
                      color: 'rgba(153,27,27,1)',
                    }}
                  >
                    Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie mich
                    telefonisch.
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Kontaktinformationen */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="space-y-8"
            >
              <div className="card-soft">
                <h3 className="font-serif text-xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Direkter Kontakt
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20" style={{ color: 'var(--color-primary-500)' }}>
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Telefon</p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>0157 / 53557860</p>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Mo-Fr: 9:00 - 18:00 Uhr</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20" style={{ color: 'var(--color-primary-500)' }}>
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>E-Mail</p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>kontakt@malneudenken.de</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20" style={{ color: 'var(--color-primary-500)' }}>
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Adresse</p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>
                        Ich arbeite ortsunabhängig in angemieteten Praxisräumen innerhalb Dresdens.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-soft">
                <h3 className="font-serif text-xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Beratungszeiten
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Montag - Freitag</span>
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      Nach Terminvereinbarung
                    </span>
                  </div>
                </div>
                <p className="text-sm mt-4" style={{ color: 'var(--color-text-muted)' }}>
                  Termine auch außerhalb der regulären Beratungszeiten nach Absprache möglich.
                </p>
              </div>

              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-subtle)',
                }}
              >
                <h4 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  Gut zu wissen
                </h4>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <li className="flex items-start gap-2">
                    <span style={{ color: 'var(--color-primary-500)' }}>•</span>
                    <span>Erstgespräche dauern in der Regel 60-90 Minuten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: 'var(--color-primary-500)' }}>•</span>
                    <span>Auch Videosprechstunden möglich</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
